import React, { useState } from 'react';
import { AudioStats } from '../utils/audioAnalysis';
import { TranscriptionResult } from '../utils/speechRecognition';
import './DiagnosticData.css';

interface DiagnosticDataProps {
  audioStats: AudioStats;
  transcription: TranscriptionResult;
}

const DiagnosticData: React.FC<DiagnosticDataProps> = ({ audioStats, transcription }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  console.log('DiagnosticData - Received transcription:', transcription);
  console.log('DiagnosticData - showDetails state:', showDetails);
  
  // Calculate various metrics
  const actualSpeakingTime = audioStats.duration * (1 - audioStats.silenceRatio);
  const actualWPM = transcription.wordCount > 0 
    ? Math.round((transcription.wordCount / actualSpeakingTime) * 60)
    : 0;
  
  const estimatedVsActualRatio = audioStats.estimatedWordsPerMinute > 0
    ? (actualWPM / audioStats.estimatedWordsPerMinute).toFixed(2)
    : 'N/A';
  
  const hasRealWords = transcription.words && transcription.words.length > 0 && 
    !transcription.transcript.startsWith('[Audio detected');
  
  const averageWordLength = hasRealWords
    ? (transcription.words.reduce((sum, word) => sum + word.length, 0) / transcription.words.length).toFixed(1)
    : 'N/A';
  
  const uniqueWords = hasRealWords ? new Set(transcription.words.map(w => w.toLowerCase())).size : 0;
  const vocabularyDiversity = hasRealWords && transcription.wordCount > 0
    ? ((uniqueWords / transcription.wordCount) * 100).toFixed(1)
    : 'N/A';
  
  // Word frequency analysis
  const wordFrequency = hasRealWords ? transcription.words.reduce((acc: { [key: string]: number }, word) => {
    const lower = word.toLowerCase();
    acc[lower] = (acc[lower] || 0) + 1;
    return acc;
  }, {}) : {};
  
  const topWords = Object.entries(wordFrequency)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  return (
    <div className="diagnostic-data">
      <div className="diagnostic-header">
        <h3>🔬 Diagnostic Data & Speech Metrics</h3>
        <button 
          className="toggle-details-btn"
          onClick={() => {
            console.log('Button clicked, toggling from', showDetails, 'to', !showDetails);
            setShowDetails(!showDetails);
          }}
          style={{
            opacity: showDetails ? 0.8 : 1,
            background: showDetails 
              ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          {showDetails ? '▼ Hide' : '▶ Show'} Transcript
        </button>
      </div>
      
      {showDetails && (
        <div className="transcript-details" style={{
          display: 'block',
          animation: 'fadeIn 0.3s ease-in',
          marginTop: '20px',
          marginBottom: '20px'
        }}>
          <h4>📜 Full Transcript</h4>
          <div className="transcript-box">
            {transcription.transcript || '[Note: Full transcription requires Web Speech API support. Use Chrome or Edge browser for real-time transcription.]'}
          </div>
          
          {hasRealWords && transcription.wordsWithTimestamps && transcription.wordsWithTimestamps.length > 0 && (
            <div className="word-list">
              <h5>Word-by-Word Analysis ({transcription.wordCount} words)</h5>
              <div className="words-container" style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginTop: '10px'
              }}>
                {transcription.wordsWithTimestamps.map((wordData: any, index: number) => {
                  const confidence = wordData.confidence || 0;
                  let backgroundColor = '#e8f5e9'; // Default green
                  if (confidence < 0.6) backgroundColor = '#ffebee'; // Red for low confidence
                  else if (confidence < 0.8) backgroundColor = '#fff3e0'; // Orange for medium
                  
                  return (
                    <span key={index} className="word-chip" style={{
                      backgroundColor,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      border: `1px solid ${confidence < 0.6 ? '#ef5350' : confidence < 0.8 ? '#ffa726' : '#4caf50'}`,
                      position: 'relative',
                      cursor: 'help'
                    }} title={`Confidence: ${(confidence * 100).toFixed(0)}%`}>
                      {wordData.word}
                      <span style={{
                        fontSize: '10px',
                        position: 'absolute',
                        top: '-8px',
                        right: '2px',
                        color: confidence < 0.6 ? '#c62828' : confidence < 0.8 ? '#f57c00' : '#2e7d32'
                      }}>
                        {(confidence * 100).toFixed(0)}%
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          
          {hasRealWords && !transcription.wordsWithTimestamps && (
            <div className="word-list">
              <h5>Detected Words ({transcription.wordCount})</h5>
              <div className="words-container">
                {transcription.words.map((word, index) => (
                  <span key={index} className="word-chip">
                    {word}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {transcription.alternativeTranscripts && transcription.alternativeTranscripts.length > 0 && (
            <div className="alternatives">
              <h5>Alternative Transcripts</h5>
              {transcription.alternativeTranscripts.map((alt, index) => (
                <div key={index} className="alternative-transcript">
                  Option {index + 1}: {alt}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      <div className="diagnostic-grid">
        <div className="diagnostic-card primary">
          <h4>📊 {hasRealWords ? 'Word Count Analysis' : 'Speech Segment Analysis'}</h4>
          <div className="metric-row">
            <span className="metric-label">{hasRealWords ? 'Actual Words Transcribed:' : 'Speech Segments Detected:'}</span>
            <span className="metric-value highlight">{transcription.wordCount}</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Estimated Words (from audio):</span>
            <span className="metric-value">
              {Math.round((audioStats.estimatedWordsPerMinute * audioStats.duration) / 60)}
            </span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Detection Method:</span>
            <span className="metric-value">{hasRealWords ? 'Web Speech API' : 'Audio Energy Analysis'}</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">{hasRealWords ? 'Transcription Confidence:' : 'Segment Detection Rate:'}</span>
            <span className="metric-value">{hasRealWords ? `${(transcription.confidence * 100).toFixed(0)}%` : `${estimatedVsActualRatio}x`}</span>
          </div>
        </div>
        
        <div className="diagnostic-card">
          <h4>⏱️ Timing Metrics</h4>
          <div className="metric-row">
            <span className="metric-label">Total Duration:</span>
            <span className="metric-value">{audioStats.duration.toFixed(2)}s</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Speaking Time:</span>
            <span className="metric-value highlight">{actualSpeakingTime.toFixed(2)}s</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Silence Time:</span>
            <span className="metric-value">
              {(audioStats.duration * audioStats.silenceRatio).toFixed(2)}s
            </span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Silence Ratio:</span>
            <span className="metric-value">{(audioStats.silenceRatio * 100).toFixed(1)}%</span>
          </div>
        </div>
        
        <div className="diagnostic-card">
          <h4>💬 Speaking Rate</h4>
          <div className="metric-row">
            <span className="metric-label">Actual WPM:</span>
            <span className="metric-value highlight">{actualWPM}</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Estimated WPM:</span>
            <span className="metric-value">{audioStats.estimatedWordsPerMinute}</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Words per Second:</span>
            <span className="metric-value">
              {(transcription.wordCount / actualSpeakingTime).toFixed(2)}
            </span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Pause Count:</span>
            <span className="metric-value">{audioStats.pauseCount}</span>
          </div>
        </div>
        
        <div className="diagnostic-card">
          <h4>📝 Vocabulary Analysis</h4>
          <div className="metric-row">
            <span className="metric-label">Unique Words:</span>
            <span className="metric-value">{uniqueWords}</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Vocabulary Diversity:</span>
            <span className="metric-value">{vocabularyDiversity}%</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Avg Word Length:</span>
            <span className="metric-value">{averageWordLength} chars</span>
          </div>
          <div className="metric-row">
            <span className="metric-label">Total Characters:</span>
            <span className="metric-value">
              {transcription.words.reduce((sum, word) => sum + word.length, 0)}
            </span>
          </div>
        </div>
      </div>
      
      {topWords.length > 0 && (
        <div className="word-frequency">
          <h4>🔤 Most Frequent Words</h4>
          <div className="frequency-list">
            {topWords.map(([word, count], index) => (
              <div key={index} className="frequency-item">
                <span className="word-rank">#{index + 1}</span>
                <span className="word-text">"{word}"</span>
                <span className="word-count">{count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="diagnostic-note">
        <p>
          <strong>Note:</strong> {hasRealWords 
            ? 'Transcription powered by Web Speech API. Words are transcribed in real-time as you speak.'
            : 'This app uses audio energy analysis to detect speech segments. For real-time transcription, please use Chrome or Edge browser.'}
        </p>
      </div>
    </div>
  );
};

export default DiagnosticData;